// ─── CCFS Leads utility helpers — extracted from AdminCCFSLeads.jsx (Phase 1) ───
// Pure functions. No behaviour change.

const _CIN_TYPE_MAP = {
  PTC: 'Private Limited',
  PLC: 'Public Limited',
  NPL: 'Section 8 (Non-Profit)',
  OPC: 'One Person Company',
  FTC: 'Foreign Subsidiary',
  ULL: 'Unlimited Liability',
  GAP: 'Government Company',
  GAT: 'Government Company',
  GOI: 'Government Company',
  SGC: 'State Government Company',
  FLC: 'Financial Lease Company',
};

export function companyTypeFromCIN(cin) {
  if (!cin) return '—';
  const c = String(cin).trim().toUpperCase();
  if (c.length < 21) return '—';
  const listed = c[0] === 'L' ? 'Listed' : 'Unlisted';
  const code = c.slice(12, 15);
  const base = _CIN_TYPE_MAP[code] || '';
  if (!base) return `${listed} Company`;
  if (code === 'NPL') return base;
  return `${base} (${listed})`;
}

export function fmt(n) {
  if (!n && n !== 0) return '—';
  return '\u20B9' + Math.round(n).toLocaleString('en-IN');
}

// ─── Robust date formatter — outputs "DD MMM YYYY" from YYYY-MM-DD, DD/MM/YYYY or DD-MM-YYYY ───
export function fmtDatePretty(s) {
  if (!s) return '—';
  try {
    const str = String(s).trim();
    const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    // ISO YYYY-MM-DD
    let m = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (m) {
      const y = +m[1], mo = +m[2], d = +m[3];
      if (mo >= 1 && mo <= 12) return `${String(d).padStart(2,'0')} ${MONTHS[mo-1]} ${y}`;
    }
    // DD/MM/YYYY or DD-MM-YYYY (Indian format)
    m = str.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{4})/);
    if (m) {
      const d = +m[1], mo = +m[2], y = +m[3];
      if (mo >= 1 && mo <= 12) return `${String(d).padStart(2,'0')} ${MONTHS[mo-1]} ${y}`;
    }
  } catch { /* fallthrough */ }
  return String(s);
}

export const flagRowClass = (flag) =>
  flag === 'red' ? 'border-l-2 border-red-400 bg-red-50/40'
  : flag === 'amber' ? 'border-l-2 border-amber-400 bg-amber-50/40'
  : '';

// ─── FY helpers — used by FilingIndexSection and LeadCard's suggested-matrix path ───

// Plan B: derive the last filed BS FY start year from date_of_balance_sheet.
// Returns null if the date is missing/unparseable.
export function getBsFyStartYear(lead) {
  const bs = lead?.mca_company_info?.date_of_balance_sheet || '';
  if (!bs) return null;
  let y, m;
  let iso = bs.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) { y = parseInt(iso[1]); m = parseInt(iso[2]); }
  else {
    let dmy = bs.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{4})$/);
    if (dmy) { y = parseInt(dmy[3]); m = parseInt(dmy[2]); }
    else return null;
  }
  // Indian FY: Apr→Mar. BS at 31/03/2023 → FY 2022-23 → start year 2022.
  return m >= 4 ? y : y - 1;
}

// Build suggested filing matrix: FS ticked for every FY ≤ last-filed-BS FY; AR always false.
export function buildSuggestedFilingMatrix(lead, fyRange) {
  const bsStart = getBsFyStartYear(lead);
  const out = {};
  for (const fy of fyRange) {
    const startYear = parseInt(String(fy).split('-')[0]);
    const tickFs = bsStart != null && !isNaN(startYear) && startYear <= bsStart;
    out[fy] = { fs: !!tickFs, ar: false };
  }
  return out;
}

// Build FS/AR matrix from the saved Filing-Index FY Verification table.
// Admin's manual verification (which factors in extensions, late filings, NCLT orders) is
// the source of truth — we deliberately do NOT auto-detect from filing dates.
//
// Mapping rules per FY row:
//   row.locked (BS-year FY, MCA-confirmed)     → fs:true, ar:true
//   row.not_required (post-cessation, n/a)     → fs:true, ar:true (no gap)
//   row.aoc4 === 'filed'                       → fs:true
//   row.mgt7 === 'filed'                       → fs:true
//   row.aoc4/mgt7 === 'not_yet_due'            → fs:false, ar:false (UI renders 'Not Yet Due')
//   row.aoc4/mgt7 === 'not_filed'              → fs:false, ar:false
// Returns null if no verification rows are saved yet, so caller can fall back to BS heuristic.
export function deriveFilingMatrixFromFYVerification(lead, fyRange) {
  const rows = lead?.filing_index?.fy_verification?.rows;
  if (!rows || !rows.length) return null;
  const byFy = {};
  for (const r of rows) { if (r && r.fy) byFy[r.fy] = r; }
  const out = {};
  for (const fy of fyRange) {
    const r = byFy[fy];
    if (!r) { out[fy] = { fs: false, ar: false }; continue; }
    const fsFiled = r.locked || r.not_required || r.aoc4 === 'filed';
    const arFiled = r.locked || r.not_required || r.mgt7 === 'filed';
    out[fy] = { fs: !!fsFiled, ar: !!arFiled };
  }
  return out;
}

export function buildFYRange(lead) {
  // Derive first FY from MCA incorporation date.
  // If date_of_incorporation is missing, return [] so the table renders an empty-state
  // hint asking the admin to upload MCA Master Data first.
  const incorpStr = lead.mca_company_info?.date_of_incorporation || '';
  if (!incorpStr) return [];
  let y, m;
  // Accept ISO (YYYY-MM-DD) or DD/MM/YYYY or DD-MM-YYYY
  let iso = incorpStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) { y = parseInt(iso[1]); m = parseInt(iso[2]); }
  else {
    let dmy = incorpStr.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{4})$/);
    if (dmy) { y = parseInt(dmy[3]); m = parseInt(dmy[2]); }
    else return [];
  }
  // Indian FY = Apr → Mar. If incorporated in Jan-Mar, FY started previous April.
  const firstFYStart = m >= 4 ? y : y - 1;
  // End FY: cessation FY (override has priority) if business stopped, else current FY.
  // EXCEPTION 1: Section 8 (NPL) companies cannot strike off — they must regularise
  //   all filings up to current FY. Cessation has no meaning for them.
  // EXCEPTION 2: Admin's intent_override of 'continue' / 'regularise' / 'dormant'
  //   beats the user's original calculator answer. Mirrors backend recalc_savings.
  const isSection8 = !!lead?.cin_info?.is_section_8;
  const intentOverride = (lead.intent_override || '').toString().toLowerCase().trim();
  const isContinueOverride = ['continue', 'regularise', 'dormant'].includes(intentOverride);
  const ignoreCessation = isSection8 || isContinueOverride;
  const status = ignoreCessation ? '' : (lead.quiz_answers?.[0] || '');
  const cessationFY = ignoreCessation ? '' : (lead.cessation_fy_override || lead.cessation_fy || '');
  let endFYStart;
  if ((status === 'closed' || status === 'close') && cessationFY) {
    const cessMatch = cessationFY.toString().replace('FY ', '').match(/^(\d{4})/);
    endFYStart = cessMatch ? parseInt(cessMatch[1]) : null;
  }
  if (!endFYStart) {
    const today = new Date();
    const ty = today.getFullYear();
    const tm = today.getMonth() + 1;
    endFYStart = tm >= 4 ? ty : ty - 1;
  }
  if (endFYStart < firstFYStart) return [];
  const out = [];
  for (let s = firstFYStart; s <= endFYStart; s++) {
    out.push(`${s}-${String(s + 1).slice(-2)}`);
  }
  return out;
}
